import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Category, Discovery } from 'react-iconly';
import WorkCard from '../components/WorkCard';
import { fetchProjects } from '../services/supabaseService';
import { useTransition } from '../context/TransitionContext';
import { slugify } from '../utils/subdomain';
import { devError } from '../utils/devLog';

const Work = ({ hideTitle = false }) => {
  const { startTransition } = useTransition();
  const [viewMode, setViewMode] = useState('grid');
  const [hoveredProject, setHoveredProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef(null);
  const nodeRef = useRef(null);

  const sectionRef = useCallback((node) => {
    const isStandaloneWorkPage = window.location.pathname === '/work';
    if (isStandaloneWorkPage) {
      setIsVisible(true);
      return;
    }

    if (nodeRef.current && observerRef.current) {
      observerRef.current.unobserve(nodeRef.current);
    }

    nodeRef.current = node;

    if (node) {
      if (!observerRef.current) {
        observerRef.current = new IntersectionObserver(
          ([entry]) => {
            setIsVisible(entry.isIntersecting);
          },
          {
            root: null,
            rootMargin: '0px',
            threshold: 0.05
          }
        );
      }
      observerRef.current.observe(node);
    }
  }, []);

  const tabsRef = useRef(null);
  const pillRef = useRef(null);
  const gridBtnRef = useRef(null);
  const listBtnRef = useRef(null);

  const updatePill = useCallback((animate = true) => {
    const pill = pillRef.current;
    const activeBtn = viewMode === 'grid' ? gridBtnRef.current : listBtnRef.current;
    if (!pill || !activeBtn) return;

    if (!animate) {
      pill.style.transition = 'none';
    }
    pill.style.width = `${activeBtn.offsetWidth}px`;
    pill.style.transform = `translateX(${activeBtn.offsetLeft}px)`;
    if (!animate) {
      // eslint-disable-next-line no-unused-expressions
      pill.offsetHeight; // force reflow
      pill.style.transition = '';
    }
  }, [viewMode]);

  // Snap pill on first paint (no animation) or after loading finishes
  useEffect(() => {
    if (!loading) {
      const id = requestAnimationFrame(() => updatePill(false));
      return () => cancelAnimationFrame(id);
    }
  }, [loading, updatePill]);

  // Animate pill on viewMode change
  useEffect(() => {
    updatePill(true);
  }, [viewMode, updatePill]);

  // Re-measure on resize
  useEffect(() => {
    const onResize = () => updatePill(false);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [updatePill]);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchProjects();
        setProjects(data || []);
      } catch (err) {
        devError('Error loading projects:', err);
        setError('Failed to load projects');
      } finally {
        setLoading(false);
      }
    };
    loadProjects();
  }, []);

  if (loading) {
    return (
      <section id="work" className="v2-section v2-container">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid var(--v2-color-taupe)',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="work" className="v2-section v2-container">
        <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--v2-color-taupe)' }}>
          <p>{error}</p>
        </div>
      </section>
    );
  }

  return (
    <section id="work" className="v2-section v2-container" ref={sectionRef}>
      {viewMode === 'grid' ? (
        <div className="v2-grid-2col">
          {projects.map((project, index) => (
            <WorkCard key={project.id} project={project} index={index} />
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '32px 0 120px 0' }}>
          {projects.map((project) => {
            const isHovered = hoveredProject === project.id;
            return (
              <div
                key={project.id}
                onMouseEnter={() => setHoveredProject(project.id)}
                onMouseLeave={() => setHoveredProject(null)}
                onClick={() => startTransition(`/work/${slugify(project.title)}`)}
                style={{
                  position: 'relative',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '4px 0',
                }}
              >
                {/* Left Logo */}
                <div style={{
                  position: 'absolute',
                  left: '15%',
                  opacity: isHovered ? 1 : 0,
                  transition: 'opacity 0.3s ease, transform 0.3s ease',
                  transform: isHovered ? 'translateX(0)' : 'translateX(-20px)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  {project.image ? (
                    <img
                      src={project.image}
                      alt={project.title}
                      style={{
                        width: '42px',
                        height: '24px',
                        borderRadius: '100px',
                        objectFit: 'cover',
                        border: '2px solid var(--v2-color-charcoal)'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '42px', height: '24px', borderRadius: '100px',
                      border: '2px solid var(--v2-color-charcoal)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '11px', fontWeight: 'bold'
                    }}>
                      {project.category?.substring(0, 2).toUpperCase() || 'PR'}
                    </div>
                  )}
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>×</span>
                </div>

                {/* Center Big Text */}
                <h2 style={{
                  fontSize: 'clamp(4rem, 9vw, 140px)',
                  fontFamily: 'var(--v2-font-display)',
                  fontWeight: 900,
                  margin: 0,
                  lineHeight: 0.85,
                  letterSpacing: '-0.04em',
                  color: isHovered ? 'var(--v2-color-charcoal)' : 'var(--v2-color-taupe)',
                  opacity: isHovered ? 1 : 0.3,
                  transition: 'color 0.3s ease, opacity 0.3s ease',
                  textAlign: 'center',
                  textTransform: 'capitalize'
                }}>
                  {project.title}
                </h2>

                {/* Right Metadata */}
                <div style={{
                  position: 'absolute',
                  right: '15%',
                  opacity: isHovered ? 1 : 0,
                  transition: 'opacity 0.3s ease, transform 0.3s ease',
                  transform: isHovered ? 'translateX(0)' : 'translateX(20px)',
                  display: 'flex',
                  gap: '40px',
                  textAlign: 'left'
                }}>
                  <div>
                    <div style={{ fontSize: '10px', color: 'var(--v2-color-taupe)', letterSpacing: '0.1em', marginBottom: '8px', fontWeight: 600 }}>YEAR</div>
                    <div style={{ fontSize: '15px', fontWeight: 500 }}>{project.year}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '10px', color: 'var(--v2-color-taupe)', letterSpacing: '0.1em', marginBottom: '8px', fontWeight: 600 }}>SECTOR</div>
                    <div style={{ fontSize: '15px', fontWeight: 500 }}>{project.category}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tabs with sliding pill */}
      <div style={{ 
        position: 'fixed', 
        bottom: '32px', 
        left: '50%', 
        transform: `translate(-50%, ${isVisible ? '0' : '100px'})`, 
        opacity: isVisible ? 1 : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
        transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.5s ease',
        display: 'flex', 
        justifyContent: 'center', 
        zIndex: 100 
      }}>
        <div className="t-tabs" ref={tabsRef} role="tablist">
          <span className="t-tabs-pill" ref={pillRef} aria-hidden="true" />
          <button
            ref={gridBtnRef}
            className="t-tab"
            role="tab"
            aria-selected={viewMode === 'grid'}
            onClick={() => setViewMode('grid')}
          >
            <Category size={20} set="light" />
            Grid View
          </button>
          <button
            ref={listBtnRef}
            className="t-tab"
            role="tab"
            aria-selected={viewMode === 'list'}
            onClick={() => setViewMode('list')}
          >
            <Discovery size={20} set="light" />
            List view
          </button>
        </div>
      </div>
    </section>
  );
};

export default Work;
